/**
 * Utility to read and parse Concord consent state from cookies
 */

interface ConcordConsentState {
  unclassified: boolean;
  marketing: boolean;
  analytics: boolean;
  strictly_necessary: boolean;
  do_not_sell: boolean;
  functional: boolean;
}

const CONCORD_COOKIE_NAME = 'concord-allow-state-971c84d1-5935-449e-bdda-cc3ab1178074';

export function getConcordConsentState(): ConcordConsentState | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    // Get the Concord consent cookie
    const cookies = document.cookie.split('; ');
    const concordCookie = cookies.find(cookie => cookie.startsWith(`${CONCORD_COOKIE_NAME}=`));
    
    if (!concordCookie) {
      console.log('Concord consent cookie not found');
      return null;
    }

    // Extract and parse the cookie value
    const cookieValue = concordCookie.split('=')[1];
    const decodedValue = decodeURIComponent(cookieValue);
    const consentState = JSON.parse(decodedValue) as ConcordConsentState;
    
    return consentState;
  } catch (error) {
    console.error('Error parsing Concord consent cookie:', error);
    return null;
  }
}

export function hasAnalyticsConsent(): boolean {
  const consentState = getConcordConsentState();
  return consentState?.analytics === true;
}

export function hasMarketingConsent(): boolean {
  const consentState = getConcordConsentState();
  return consentState?.marketing === true;
}

// Listen for consent changes
export function onConsentChange(callback: (consentState: ConcordConsentState) => void): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }

  // Check for consent changes periodically
  let lastConsentString = JSON.stringify(getConcordConsentState());
  
  const checkInterval = setInterval(() => {
    const currentConsent = getConcordConsentState();
    const currentConsentString = JSON.stringify(currentConsent);
    
    if (currentConsentString !== lastConsentString && currentConsent) {
      lastConsentString = currentConsentString;
      callback(currentConsent);
    }
  }, 1000);

  // Also listen for Concord events if available
  const handleConcordUpdate = (_event: any) => {
    const consentState = getConcordConsentState();
    if (consentState) {
      callback(consentState);
    }
  };

  window.addEventListener('concord-consent-updated', handleConcordUpdate);

  // Return cleanup function
  return () => {
    clearInterval(checkInterval);
    window.removeEventListener('concord-consent-updated', handleConcordUpdate);
  };
} 